import {
  GetAssetForecastDocument,
  GetInstallationForecastDocument,
} from "@hydrologiq/gql-experience/graphql"
import type { RunDryForecast, ForecastUnavailable } from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

// Type guard to check if response is RunDryForecast
function isRunDryForecast(
  response: RunDryForecast | ForecastUnavailable | null | undefined,
): response is RunDryForecast {
  return response != null && "forecast" in response && Array.isArray(response.forecast)
}

interface UseCombinedForecastResult {
  dailyForecast?: RunDryForecast
  hourlyForecast?: RunDryForecast
  dailyForecastUnavailable?: ForecastUnavailable
  hourlyForecastUnavailable?: ForecastUnavailable
  loading: boolean
  refreshing: boolean
  error?: Error
  refetch: () => void
}

interface AssetForecastParams {
  entityType: "asset"
  assetId: string
  days?: number
  hours?: number
}

interface InstallationForecastParams {
  entityType: "installation"
  installationId: string
  siteId?: string
  customerId?: string
  days?: number
  hours?: number
}

type ForecastParams = AssetForecastParams | InstallationForecastParams

export function useCombinedForecast(params: ForecastParams): UseCombinedForecastResult {
  const days = params.days ?? 30
  const hours = params.hours ?? 336

  const isAsset = params.entityType === "asset"
  const document = isAsset ? GetAssetForecastDocument : GetInstallationForecastDocument
  const variables = isAsset
    ? { assetId: (params as AssetForecastParams).assetId, days, hours }
    : {
        installationId: (params as InstallationForecastParams).installationId,
        days,
        hours,
        siteId: (params as InstallationForecastParams).siteId ?? null,
        customerId: (params as InstallationForecastParams).customerId ?? null,
      }
  const entityId = isAsset
    ? (params as AssetForecastParams).assetId
    : (params as InstallationForecastParams).installationId

  const { data, loading, error, refetch, networkStatus } = useQueryWithEndpoint(
    document,
    {
      variables,
      skip: !entityId,
      errorPolicy: "all",
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
    "primary",
  )

  // networkStatus 4 = refetch in background (already have cached data)
  const refreshing = networkStatus === 4

  const response = isAsset ? data?.getAssetForecast : data?.getInstallationForecast
  const dailyResponse = response?.daily
  const hourlyResponse = response?.hourly

  return {
    dailyForecast: isRunDryForecast(dailyResponse) ? dailyResponse : undefined,
    hourlyForecast: isRunDryForecast(hourlyResponse) ? hourlyResponse : undefined,
    dailyForecastUnavailable: dailyResponse && !isRunDryForecast(dailyResponse)
      ? (dailyResponse as ForecastUnavailable)
      : undefined,
    hourlyForecastUnavailable: hourlyResponse && !isRunDryForecast(hourlyResponse)
      ? (hourlyResponse as ForecastUnavailable)
      : undefined,
    loading: loading && !refreshing,
    refreshing,
    error: error ? new Error(error.message) : undefined,
    refetch,
  }
}
